import { PolicyIntegrity } from '@Core/models/scp-policy';
import { FaultResult } from '@Core/models/policy-results/policy-result';
import { isServerPolicyBusy } from '@Core/models/server-policy';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { PoliciesState } from './policies-reducer';

const getPoliciesFeatureState =
  createFeatureSelector<PoliciesState>('policies');

export const getOpenPolicies = createSelector(
  getPoliciesFeatureState,
  ({ openPolicies }) => openPolicies,
);

export const getOpenPolicyFaults = createSelector(
  getOpenPolicies,
  (openPolicies) =>
    openPolicies.reduce(
      (p, { serverPolicy }) =>
        serverPolicy?.faults.length ? p.concat(serverPolicy.faults) : p,
      [] as FaultResult[],
    ),
);

export const getCurrentOpenPolicy = createSelector(
  getPoliciesFeatureState,
  ({ openPolicies, currentOpenPolicyId }) =>
    openPolicies.find((x) => currentOpenPolicyId === x.policy.id),
);

export const getCurrentServerPolicy = createSelector(
  getCurrentOpenPolicy,
  (p) => p?.serverPolicy,
);

export const getCurrentPolicyContext = createSelector(
  getCurrentServerPolicy,
  (r) => r?.context,
);

export const isBusy = createSelector(getCurrentServerPolicy, (r) =>
  isServerPolicyBusy(r),
);

export const getPolicyRating = createSelector(
  getCurrentServerPolicy,
  (r) => r?.policyRatings,
);

export const getDocStatus = createSelector(
  getCurrentServerPolicy,
  (r) => r?.docStatus,
);

export const getFootnotes = createSelector(
  getCurrentServerPolicy,
  (p) => p?.footnotes,
);

export const getPolicyForms = createSelector(getCurrentServerPolicy, (p) => {
  return p?.policyFormInfo?.formsOnPolicy ?? [];
});

export const getAvailableForms = createSelector(getCurrentServerPolicy, (p) => {
  return p?.policyFormInfo?.forms ?? [];
});

export const getFocusFieldId = createSelector(
  getCurrentServerPolicy,
  (p) => p?.focusFieldId ?? '',
);

export const getPolicyFormsApplicabilityFilter = createSelector(
  getCurrentServerPolicy,
  (p) => {
    return p?.policyFormInfo?.applicabilityFilter ?? [];
  },
);

export const getCurrentPolicyStartingResult = createSelector(
  getCurrentServerPolicy,
  (r) => r?.policyRatingStarted,
);

export const isNewBusinessPolicy = createSelector(getCurrentOpenPolicy, (p) => {
  const newTrans = p?.policy.newTransactionInfo;
  if (newTrans && newTrans.TransactionType !== undefined) {
    return true;
  }
  return false;
});

export const isCommittedIssueAuditPolicy = createSelector(
  getCurrentOpenPolicy,
  (p) => {
    const newTrans = p?.policy.newTransactionInfo;
    if (
      p?.policy.policyPrimaryStatus === 'Issue' &&
      p?.policy.policyIntegrity === PolicyIntegrity.Committed &&
      newTrans &&
      newTrans.TransactionType === 'audit'
    ) {
      return true;
    }
    return false;
  },
);
